<script lang="ts" context="module">
    export function setArticleConfigAttr(
        articleConfig: ArticleConfig,
        key: string,
        value: any,
        prop: string = "value"
    ) {
        if (typeof articleConfig.customAttributes !== "object") {
            articleConfig.customAttributes = {}
        }
        if (typeof articleConfig.customAttributes[key] !== "object") {
            articleConfig.customAttributes[key] = {}
        }
        articleConfig.customAttributes[key][prop] = value
    }

    export async function checkSiid(
        cardType: string,
        siid: string
    ): Promise<{ isValid: boolean; canBeOrdered: boolean }> {
        try {
            const r = await api("action", {
                params: {
                    cmd: "checkSiid",
                    cardType,
                    siid,
                },
            })

            return r.data
        } catch (e) {
            console.error(e)
            return { isValid: false, canBeOrdered: false }
        }
    }

    export function validateCustomAttribute(
        articleConfig: ArticleConfig,
        attr: ProductExternalDataCustomAttribute,
        initialConfiguration?: { [key: string]: string },
        excludedCustomAttributes?: string[]
    ): boolean {
        if (typeof articleConfig.customAttributes !== "object") {
            articleConfig.customAttributes = {}
        }
        const key = attr.key
        if (typeof articleConfig.customAttributes?.[key] !== "object") {
            articleConfig.customAttributes[key] = {}
        }

        // remove excluded custom attributes
        if (excludedCustomAttributes?.includes(key)) {
            console.log("removing from custom attributes", key)
            articleConfig.customAttributes[key] = {}
            return true
        }

        const attrInput = articleConfig.customAttributes[key]
        if (attr.isRequired) {
            if (typeof attrInput.value === "undefined") {
                let defaultValue = initialConfiguration?.[key]
                if (!defaultValue) {
                    defaultValue = attr.defaultValue
                }
                if (!defaultValue && attr.type === "selectOne") {
                    defaultValue = attr.options?.[0]?.value
                }

                if (defaultValue) {
                    setArticleConfigAttr(articleConfig, key, defaultValue)
                }
                console.log("set default to", attr.key, attrInput.value)
            }
            setValidationFailure(articleConfig, key, "required", !attrInput.value)
        }

        return !attrInput.validationFailures?.length
    }
</script>

<script lang="ts">
    import SiCardSuggestNumberDialog from "./SiCardSuggestNumberDialog.svelte"
    import { api } from "../../api"
    import pDebounce from "p-debounce"
    import { duplicateSiidInCart, setValidationFailure } from "./CartButton.svelte"
    import _, { debounce } from "lodash"
    import Loader from "./Loader.svelte"

    export let articleConfig: ArticleConfig
    export let excludedCustomAttributes: string[] = undefined
    export let initialConfiguration: { [key: string]: string } = undefined

    export let quantity: number = 1

    const checkSiidDebounced = pDebounce(checkSiid, 1000)

    const uniqueID = Math.random().toString(36).substr(2, 9)

    function getFailureMessage(f: string): string {
        switch (f) {
            case "duplicate":
                return "Duplicate number"
            case "invalid":
                return "Invalid number"
            case "inUse":
                return "Number already in use"
            case "required":
                return "Input required"
            default:
                return "Unknown error"
        }
    }

    const rerender = debounce(() => {
        articleConfig = articleConfig
        console.log("rerender", articleConfig)
    }, 500)

    function setAttr(key: string, value: any, prop: string = "value") {
        if (typeof value === "string") {
            value = value.replace(/[^\x20-\x7E]/g, "")
        }

        setArticleConfigAttr(articleConfig, key, value, prop)

        // reactivity
        rerender()
    }

    function validateBomItemCustomAttribute(articleConfig: ArticleConfig, attr: ProductExternalDataCustomAttribute) {
        if (attr.applicableToQuantityOne && quantity > 1) {
            // dont check hidden attributes
            return true
        }
        if (attr.applicableToQuantityGreaterThanOne && quantity === 1) {
            // dont check hidden attributes
            return true
        }
        return validateCustomAttribute(articleConfig, attr, initialConfiguration, excludedCustomAttributes)
    }

    // initialize and validate custom attributes config
    $: {
        let _valid = true
        articleConfig?.article?.customAttributes?.forEach((attr) => {
            const attrValid = validateBomItemCustomAttribute(articleConfig, attr)
            if (!attrValid) {
                console.log("invalid custom attr", attr, articleConfig)
            }
            _valid = _valid && attrValid
        })

        articleConfig.valid = _valid
    }

    function getValue(attr: ProductExternalDataCustomAttribute): any {
        const value = articleConfig.customAttributes?.[attr.key]?.value
        if (typeof value !== "undefined") {
            return value
        }
        const _v = initialConfiguration?.[attr.key] || attr.defaultValue || ""
        if (_v !== value) {
            setAttr(attr.key, _v)
        }
        return _v
    }
</script>

{#if articleConfig?.article?.customAttributes?.length}
    <div class="offset-2 col-auto custom-attributes">
        <div class="grid form">
            {#each articleConfig.article.customAttributes as attr}
                {#if !excludedCustomAttributes?.includes(attr.key)}
                    {#if (attr.applicableToQuantityGreaterThanOne && quantity > 1) || (attr.applicableToQuantityOne && quantity === 1)}
                        <div class="col-grid" class:border-bottom="{attr.type != 'boolean'}">
                            {#if attr.type != "boolean"}<label for="{uniqueID + attr.key}"
                                    >{attr.label} {attr.isRequired ? "*" : ""}</label
                                >{/if}
                            {#if attr.type == "selectOne"}
                                <select
                                    on:change="{(e) => {
                                        // @ts-ignore
                                        setAttr(attr.key, e.target.value)
                                    }}"
                                >
                                    {#each attr.options || [] as o}
                                        <option value="{o.value}" selected="{getValue(attr) === o.value}"
                                            >{o.label}</option
                                        >
                                    {/each}
                                </select>
                            {:else if attr.type == "boolean"}
                                <input
                                    id="{uniqueID + attr.key}"
                                    type="checkbox"
                                    checked="{getValue(attr) === true || getValue(attr) === 'true'}"
                                    on:change="{(e) => {
                                        // @ts-ignore
                                        setAttr(attr.key, e.target.checked)
                                    }}"
                                />
                                <label class="checkbox-wrap" for="{uniqueID + attr.key}"
                                    ><span>{attr.label} {attr.isRequired ? "*" : ""}</span></label
                                >
                            {:else if attr.key === "sportidentId"}
                                <span
                                    >Number between {articleConfig.article.properties?.sportidentIdRangeMin} and {articleConfig
                                        .article?.properties?.sportidentIdRangeMax}</span
                                >
                                {#if articleConfig.customAttributes?.[attr.key]?.validationFailures?.length}
                                    {#each articleConfig.customAttributes?.[attr.key]?.validationFailures as failure}
                                        <span class="error">{getFailureMessage(failure)}</span>
                                    {/each}
                                {/if}
                                <div class="grid">
                                    <div class="col-md-6 mobile-border-bottom">
                                        <input
                                            type="text"
                                            class:invalid="{!!articleConfig.customAttributes?.[attr.key]
                                                ?.validationFailures?.length}"
                                            value="{getValue(attr)}"
                                            on:change="{(e) => {
                                                // @ts-ignore
                                                setAttr(attr.key, e.target.value)
                                                duplicateSiidInCart()
                                            }}"
                                            on:keyup="{(e) => {
                                                // @ts-ignore
                                                const v = e.target.value
                                                setAttr(attr.key, v)
                                                duplicateSiidInCart()
                                                if (v) {
                                                    attr._validating = true
                                                    checkSiidDebounced(articleConfig.article?.properties?.cardType, v)
                                                        .then((r) => {
                                                            // @ts-ignore
                                                            if (!r._processed) {
                                                                console.log(
                                                                    r,
                                                                    articleConfig.customAttributes?.[attr.key]?.value
                                                                )
                                                                // check current v after promise resolves if empty value
                                                                const isEmpty =
                                                                    !articleConfig.customAttributes?.[attr.key]?.value

                                                                setValidationFailure(
                                                                    articleConfig,
                                                                    attr.key,
                                                                    'invalid',
                                                                    !isEmpty && !r.isValid
                                                                )
                                                                setValidationFailure(
                                                                    articleConfig,
                                                                    attr.key,
                                                                    'inUse',
                                                                    !isEmpty && r.isValid && !r.canBeOrdered
                                                                )

                                                                // @ts-ignore
                                                                r._processed = true

                                                                // reactivity instant
                                                                articleConfig = articleConfig
                                                            }
                                                        })
                                                        .finally(() => {
                                                            attr._validating = false
                                                        })
                                                } else {
                                                    setValidationFailure(articleConfig, attr.key, 'invalid', false)
                                                    setValidationFailure(articleConfig, attr.key, 'inUse', false)
                                                }
                                                // reactivity
                                                articleConfig = articleConfig
                                            }}"
                                        />
                                        {#if attr._validating}
                                            <div class="loading-input">
                                                <Loader />
                                            </div>
                                        {/if}
                                        {#if articleConfig.customAttributes?.[attr.key]?.validationFailures?.length}
                                            <img
                                                class="validation-icon"
                                                src="/assets/icons/lucide_info.svg"
                                                alt="Validation error"
                                            />
                                        {/if}
                                    </div>
                                    <div class="col-md-6">
                                        <SiCardSuggestNumberDialog
                                            cardType="{articleConfig.article?.properties?.cardType}"
                                            on:selected="{(n) => {
                                                setAttr(attr.key, n.detail)
                                                setValidationFailure(articleConfig, attr.key, 'invalid', false)
                                                setValidationFailure(articleConfig, attr.key, 'inUse', false)
                                                // reactivity
                                                articleConfig = articleConfig
                                                duplicateSiidInCart()
                                            }}"
                                        />
                                    </div>
                                </div>
                            {:else}
                                <input
                                    class:invalid="{!!articleConfig.customAttributes?.[attr.key]?.validationFailures
                                        ?.length}"
                                    type="text"
                                    value="{getValue(attr)}"
                                    on:change="{(e) => {
                                        // @ts-ignore
                                        setAttr(attr.key, e.target.value)
                                    }}"
                                    on:keyup="{(e) => {
                                        // @ts-ignore
                                        setAttr(attr.key, e.target.value)
                                    }}"
                                    required="{attr.isRequired}"
                                />
                            {/if}
                        </div>
                    {/if}
                {/if}
            {/each}
        </div>
    </div>
{/if}

<style>
    input.invalid {
        animation: blink 1s linear infinite;
        margin-bottom: -1px;
        & + .validation-icon {
            display: block;
        }
    }

    @keyframes blink {
        0% {
            border-bottom: 2px solid red;
        }
        50% {
            border-bottom: 2px solid transparent;
        }
        100% {
            border-bottom: 2px solid red;
        }
    }
</style>
